import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { auditsAPI, type AuditAction, type SupplierAuditDetail } from '../../lib/api/audits';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { AlertCircle, ArrowLeft, ClipboardList, Pencil, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '../barratt/uploadCycle';
import { AuditOutcomeBadge, AuditStatusBadge } from './auditStatus';
import ActionsTable from './ActionsTable';
import AuditDialog from './AuditDialog';
import ActionDialog from './ActionDialog';

/** One audit: its facts, findings, checklist questionnaire and actions. */
export default function AuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { portalMode } = useView();
  const readOnly = portalMode === 'supplier';
  const [audit, setAudit] = useState<SupplierAuditDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<AuditAction | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    auditsAPI.get(Number(id))
      .then((res) => setAudit(res.data))
      .catch((err: unknown) => {
        console.error('Audit load failed:', err);
        setError(getErrorMessage(err, 'Could not load this audit'));
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const cancelAudit = async () => {
    if (!audit) return;
    try {
      const res = await auditsAPI.update(audit.id, { status: 'cancelled' });
      setAudit(res.data);
      toast.success(`${audit.reference} cancelled — its open actions were cancelled with it`);
    } catch (err: unknown) {
      console.error('Cancel audit failed:', err);
      toast.error(getErrorMessage(err, 'Could not cancel the audit'));
    } finally {
      setCancelOpen(false);
    }
  };

  const completeAction = async (action: AuditAction) => {
    try {
      await auditsAPI.completeAction(action.id);
      toast.success(`"${action.title}" completed`);
      load();
    } catch (err: unknown) {
      console.error('Complete action failed:', err);
      toast.error(getErrorMessage(err, 'Could not complete the action'));
    }
  };

  if (error) {
    return (
      <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-sm font-medium">Could not load this audit</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/audits')}>Back to audits</Button>
        </div>
      </CardContent></Card>
    );
  }
  if (loading || !audit) {
    return (
      <div className="space-y-6" data-testid="audit-detail-loading">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-48 animate-pulse rounded bg-muted" />
          <div className="h-48 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  const facts: Array<[string, React.ReactNode]> = [
    ['Supplier', <Link to={`/suppliers/${audit.supplier}`} className="text-primary hover:underline">{audit.supplier_name}</Link>],
    ['Division', audit.division_name ?? 'Group-wide'],
    ['Type', audit.audit_type_display],
    ['Scheduled', formatDay(audit.scheduled_date)],
    ['Completed', formatDay(audit.completed_date)],
    ['Auditor', audit.auditor_name],
    ['Outcome', <AuditOutcomeBadge outcome={audit.outcome} />],
    ['Score', audit.score !== null ? <><span className="font-semibold">{Number(audit.score).toFixed(1)}</span><span className="text-muted-foreground"> / 100</span></> : '—'],
  ];
  const checklist = audit.checklist_summary;
  const isOpen = audit.status === 'planned' || audit.status === 'in_progress';

  return (
    <div className="space-y-6" data-testid="audit-detail">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-1" onClick={() => navigate('/audits')}>
            <ArrowLeft className="mr-1 h-4 w-4" />Audits
          </Button>
          <h1 className="text-2xl font-bold text-foreground">{audit.reference} · {audit.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <AuditStatusBadge status={audit.status} overdue={audit.is_overdue} />
            <span className="text-sm text-muted-foreground">{audit.customer_name}</span>
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="audit-edit">
              <Pencil className="mr-2 h-4 w-4" />Edit
            </Button>
            {isOpen && (
              <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} data-testid="audit-cancel">
                <XCircle className="mr-2 h-4 w-4" />Cancel audit
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-lg">Details</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {facts.map(([label, value]) => (
              <div key={label} className="grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-x-4 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-lg">Scope and findings</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h2 className="text-base font-semibold text-foreground">Scope and purpose</h2>
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{audit.summary || 'Nothing recorded yet.'}</p>
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Findings</h2>
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{audit.findings || 'Nothing recorded yet.'}</p>
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Checklist</h2>
              {checklist ? (
                <div className="mt-1 flex flex-wrap items-center gap-3 text-muted-foreground">
                  <ClipboardList className="h-4 w-4" />
                  <span className="text-foreground">{checklist.name}</span>
                  <span>{checklist.status.replace(/_/g, ' ')} · <span className="font-semibold text-foreground">{checklist.completion_percentage.toFixed(0)}%</span> answered
                    {checklist.score_percentage !== null && <> · score <span className="font-semibold text-foreground">{checklist.score_percentage.toFixed(0)}%</span></>}</span>
                  <Link to={readOnly ? `/onboarding/fill/${checklist.id}` : `/onboarding/review/${checklist.id}`} className="text-primary hover:underline" data-testid="audit-checklist-link">
                    {readOnly ? 'Open questionnaire' : 'Review answers'}
                  </Link>
                </div>
              ) : (
                <p className="mt-1 text-muted-foreground">No questionnaire attached{readOnly ? '.' : ' — pick one under Edit to use it as the checklist.'}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">Actions <span className="font-normal text-muted-foreground">({audit.actions.length})</span></h2>
          {!readOnly && isOpen && (
            <Button size="sm" onClick={() => { setEditingAction(null); setActionOpen(true); }} data-testid="audit-raise-action">Raise action</Button>
          )}
        </div>
        <ActionsTable
          actions={audit.actions} loading={false} readOnly={readOnly} showAudit={false} compact
          onEdit={(a) => { setEditingAction(a); setActionOpen(true); }}
          onComplete={completeAction}
        />
      </div>

      {!readOnly && (
        <>
          <AuditDialog open={editOpen} onClose={() => setEditOpen(false)} audit={audit} onSaved={setAudit} />
          <ActionDialog open={actionOpen} onClose={() => setActionOpen(false)} action={editingAction}
                        audit={{ id: audit.id, supplier: audit.supplier, supplier_name: audit.supplier_name, reference: audit.reference }} onSaved={load} />
          <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel {audit.reference}?</AlertDialogTitle>
                <AlertDialogDescription>
                  The audit is marked cancelled and its open actions are cancelled with it. Completed actions are kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={cancelAudit} data-testid="audit-cancel-confirm">Cancel audit</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
